import type { Id, Profile } from '../lib/types';
import { buildCustomTheme, CUSTOM_THEME_KEY } from './customTheme';
import { DEFAULT_THEME, getTheme, THEMES } from './registry';
import { resolveThemeKey } from './resolveThemeKey';
import type { CustomTheme, Theme } from './types';

// Resolve a profile's active Theme, branching on the legacy
// `CUSTOM_THEME_KEY` sentinel. Falls back to `DEFAULT_THEME` when
// `theme.default === 'custom'` but `theme.custom` is missing or invalid
// — never throws so a half-written tree can't crash a kid mid-tap.
//
// This signature is retained for boot code and tests that don't have
// access to the family library. Store-aware consumers
// (`useActiveTheme`) should call `resolveThemeWithLibrary` instead so a
// theme id pointing at `SyncedTree.customThemes` resolves properly.
export function resolveTheme(profile: Profile, routineId?: Id): Theme {
  const key = resolveThemeKey(profile, routineId);
  if (key !== CUSTOM_THEME_KEY) return getTheme(key);
  const recipe = profile.theme.custom;
  if (!recipe) return DEFAULT_THEME;
  try {
    return buildCustomTheme(recipe);
  } catch {
    return DEFAULT_THEME;
  }
}

// Resolve against the preset registry, then the family theme library,
// then the pre-v4 inline recipe (kept for one release per tasks/46 §D6).
// A theme id that no longer exists (stale reference after
// `deleteCustomTheme`) falls back to the default theme rather than
// throwing.
export function resolveThemeWithLibrary(
  profile: Profile,
  routineId: Id | undefined,
  customThemes: readonly CustomTheme[],
): Theme {
  const key = resolveThemeKey(profile, routineId);

  const preset = THEMES.find((t) => t.key === key);
  if (preset) return preset;

  const custom = customThemes.find((c) => c.id === key);
  if (custom) {
    try {
      return buildCustomTheme(custom);
    } catch {
      return DEFAULT_THEME;
    }
  }

  if (key === CUSTOM_THEME_KEY && profile.theme.custom) {
    try {
      return buildCustomTheme(profile.theme.custom);
    } catch {
      return DEFAULT_THEME;
    }
  }

  return DEFAULT_THEME;
}
